import { useEffect, useState } from 'react'
import { api, upload } from '../api.js'
import BlocForm from '../components/BlocForm.jsx'

const COULEURS = ['#1B3B8F', '#0E5B4A', '#8A3324', '#5B2B82', '#0B5566', '#7A1F3D', '#2C4A22', '#3F3F46']

// Contraste : la mairie choisit une couleur, on refuse de descendre sous 4,5 pour 1.
const hex2rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
const lum = (h) => {
  const s = hex2rgb(h).map((v) => (v /= 255) <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2]
}
const contraste = (a, b) => (Math.max(lum(a), lum(b)) + 0.05) / (Math.min(lum(a), lum(b)) + 0.05)
const assombrir = (h, p) => '#' + hex2rgb(h).map((v) =>
  Math.round(v * (1 + p)).toString(16).padStart(2, '0')).join('')

function corriger(couleur) {
  let c = couleur, corrige = false, garde = 0
  while (contraste(c, '#ffffff') < 4.5 && garde++ < 24) { c = assombrir(c, -0.06); corrige = true }
  return { couleur: c, corrige, ratio: Math.round(contraste(c, '#ffffff') * 10) / 10,
           origine: Math.round(contraste(couleur, '#ffffff') * 10) / 10 }
}

export default function Apparence() {
  const [site, setSite] = useState(null)
  const [catalogue, setCatalogue] = useState(null)
  const [dispositions, setDispositions] = useState(null)
  const [blocEdite, setBlocEdite] = useState(null)   // { bloc, sectionId, colonne }
  const [ajout, setAjout] = useState(null)           // { sectionId, colonne }
  const [nouvelleSection, setNouvelleSection] = useState(false)
  const [message, setMessage] = useState('')

  const charger = () => { api.site().then(setSite) }
  useEffect(() => {
    charger()
    api.blockCatalog().then(setCatalogue).catch(() => setCatalogue(null))
    api.sectionCatalog().then(setDispositions).catch(() => setDispositions(null))
  }, [])
  if (!site || !catalogue || !dispositions) return <p className="chargement">Chargement…</p>

  const verdict = corriger(site.theme.primary || '#1B3B8F')
  const sections = site.blocks || []

  const sauver = (patch) => { setSite({ ...site, ...patch }); api.saveSite(patch) }
  const majSections = (blocks) => sauver({ blocks })

  // ---------- sections ----------
  function ajouterSection(layout) {
    const nb = dispositions.layouts.find((l) => l.key === layout).colonnes.length
    majSections([...sections, {
      id: `sec_${Date.now()}`, type: 'section', layout, fond: 'blanc', on: true,
      colonnes: Array.from({ length: nb }, () => []),
    }])
    setNouvelleSection(false)
  }

  function changerDisposition(section, layout) {
    const nb = dispositions.layouts.find((l) => l.key === layout).colonnes.length
    const actuelles = section.colonnes || []
    let colonnes
    if (nb >= actuelles.length) {
      colonnes = [...actuelles, ...Array.from({ length: nb - actuelles.length }, () => [])]
    } else {
      // on ne perd rien : ce qui dépasse rejoint la dernière colonne conservée
      colonnes = actuelles.slice(0, nb)
      colonnes[nb - 1] = [...colonnes[nb - 1], ...actuelles.slice(nb).flat()]
    }
    majSections(sections.map((s) => (s.id === section.id ? { ...s, layout, colonnes } : s)))
  }

  function deplacerSection(index, sens) {
    const cible = index + sens
    if (cible < 0 || cible >= sections.length) return
    const copie = [...sections]
    ;[copie[index], copie[cible]] = [copie[cible], copie[index]]
    majSections(copie)
  }

  function supprimerSection(section) {
    const contenu = (section.colonnes || []).flat().length
    if (contenu && !confirm(`Cette section contient ${contenu} bloc(s). Les supprimer ?`)) return
    majSections(sections.filter((s) => s.id !== section.id))
  }

  // ---------- blocs ----------
  function enregistrerBloc(bloc) {
    const { sectionId, colonne } = blocEdite
    majSections(sections.map((s) => {
      if (s.id !== sectionId) return s
      const colonnes = s.colonnes.map((col, i) => {
        if (i !== colonne) return col
        return col.some((b) => b.id === bloc.id)
          ? col.map((b) => (b.id === bloc.id ? bloc : b))
          : [...col, bloc]
      })
      return { ...s, colonnes }
    }))
    setBlocEdite(null)
  }

  function supprimerBloc(bloc) {
    const { sectionId, colonne } = blocEdite
    majSections(sections.map((s) => (s.id !== sectionId ? s : {
      ...s, colonnes: s.colonnes.map((col, i) =>
        (i === colonne ? col.filter((b) => b.id !== bloc.id) : col)),
    })))
    setBlocEdite(null)
  }

  function deplacerBloc(section, colonne, index, sens) {
    const col = [...section.colonnes[colonne]]
    const cible = index + sens
    if (cible < 0 || cible >= col.length) return
    ;[col[index], col[cible]] = [col[cible], col[index]]
    majSections(sections.map((s) => (s.id !== section.id ? s : {
      ...s, colonnes: s.colonnes.map((c, i) => (i === colonne ? col : c)),
    })))
  }

  function changerColonne(section, depuis, index, vers) {
    const bloc = section.colonnes[depuis][index]
    majSections(sections.map((s) => (s.id !== section.id ? s : {
      ...s,
      colonnes: s.colonnes.map((col, i) => {
        if (i === depuis) return col.filter((_, j) => j !== index)
        if (i === vers) return [...col, bloc]
        return col
      }),
    })))
  }

  return (
    <>
      <h1>Apparence du portail</h1>
      <p className="chapo">Composez votre page comme un site : des sections, des colonnes, et
        dedans ce que vous voulez. Les couleurs et la lisibilité sont vérifiées pour vous.</p>
      {message && <p className="succes" role="status">{message}</p>}

      <section className="carte">
        <h2>Partir d'une mise en page</h2>
        <p className="aide">UCIVIS compose une page cohérente avec votre couleur. Vos textes déjà
          écrits sont conservés.</p>
        <div className="grille">
          {catalogue.layouts.map((m) => (
            <article className="fiche" key={m.key}>
              <h3>{m.titre}</h3>
              <p>{m.description}</p>
              <button className="btn sm pri" onClick={async () => {
                const r = await api.applyLayout(m.key)
                setMessage(r.message); charger()
              }}>Utiliser</button>
            </article>
          ))}
        </div>
      </section>

      <div className="deux">
        <section>
          <Couleurs site={site} verdict={verdict} sauver={sauver} />
          <Identite site={site} sauver={sauver} />

          <div className="carte">
            <h2>Votre page</h2>
            <p className="aide">Chaque section se découpe en colonnes. Ajoutez-y des blocs, ou
              déplacez-les d'une colonne à l'autre.</p>

            {sections.map((section, index) => (
              <div className={`section-edit fond-${section.fond || 'blanc'}`} key={section.id}>
                <header>
                  <select value={section.layout || '1'} aria-label="Disposition"
                          onChange={(e) => changerDisposition(section, e.target.value)}>
                    {dispositions.layouts.map((l) => (
                      <option key={l.key} value={l.key}>{l.titre}</option>
                    ))}
                  </select>
                  <select value={section.fond || 'blanc'} aria-label="Fond"
                          onChange={(e) => majSections(sections.map((s) =>
                            (s.id === section.id ? { ...s, fond: e.target.value } : s)))}>
                    {dispositions.fonds.map((f) => (
                      <option key={f.key} value={f.key}>{f.titre}</option>
                    ))}
                  </select>
                  <div className="pousse" />
                  <span className="fleches">
                    <button aria-label="Monter la section" disabled={index === 0}
                            onClick={() => deplacerSection(index, -1)}>▲</button>
                    <button aria-label="Descendre la section" disabled={index === sections.length - 1}
                            onClick={() => deplacerSection(index, 1)}>▼</button>
                  </span>
                  <label className="bascule">
                    <input type="checkbox" checked={section.on !== false}
                           onChange={(e) => majSections(sections.map((s) =>
                             (s.id === section.id ? { ...s, on: e.target.checked } : s)))} />
                    <span className="sr">Afficher cette section</span>
                  </label>
                  <button className="btn sm dgr" onClick={() => supprimerSection(section)}>Retirer</button>
                </header>

                <div className={`colonnes-edit col-${section.layout || '1'}`}>
                  {(section.colonnes || []).map((col, i) => (
                    <div className="colonne-edit" key={i}>
                      {col.map((b, j) => (
                        <div className="bloc-edit" key={b.id}>
                          <span className="lib">{libelle(catalogue, b.type)}</span>
                          <span className="fleches">
                            <button aria-label="Monter" disabled={j === 0}
                                    onClick={() => deplacerBloc(section, i, j, -1)}>▲</button>
                            <button aria-label="Descendre" disabled={j === col.length - 1}
                                    onClick={() => deplacerBloc(section, i, j, 1)}>▼</button>
                          </span>
                          {section.colonnes.length > 1 && (
                            <select value={i} aria-label="Colonne"
                                    onChange={(e) => changerColonne(section, i, j, Number(e.target.value))}>
                              {section.colonnes.map((_, k) => (
                                <option key={k} value={k}>col. {k + 1}</option>
                              ))}
                            </select>
                          )}
                          <button className="btn sm" onClick={() => setBlocEdite({
                            bloc: b, sectionId: section.id, colonne: i })}>Régler</button>
                        </div>
                      ))}
                      <button className="btn sm ajout" onClick={() => setAjout({
                        sectionId: section.id, colonne: i })}>Ajouter ici</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {!sections.length && <p className="aide">Votre page est vide. Ajoutez une section.</p>}

            <button className="btn ajout" onClick={() => setNouvelleSection(true)}>
              Ajouter une section
            </button>
          </div>
        </section>

        <aside className="carte apercu">
          <h2>Aperçu</h2>
          <div className="ecran" style={{ '--pri': verdict.couleur }}>
            <div className="faux-nav">{site.theme.logo_text || 'MM'}</div>
            <div className="faux-hero">
              <strong>{trouverHero(sections)?.title}</strong>
              <p>{trouverHero(sections)?.lede}</p>
            </div>
            <div className="faux-corps">
              {sections.filter((s) => s.on !== false).map((s) => (
                <div className={`faux-section col-${s.layout || '1'}`} key={s.id}>
                  {(s.colonnes || []).map((col, i) => (
                    <div key={i}>
                      {col.filter((b) => b.type !== 'hero').map((b) => (
                        <div className="faux-bloc" key={b.id}>{libelle(catalogue, b.type)}</div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {nouvelleSection && (
        <div className="fenetre" role="dialog" aria-label="Ajouter une section">
          <div className="fenetre-corps large">
            <h2>Comment découper cette section ?</h2>
            <p className="aide">Sur téléphone, les colonnes s'empilent automatiquement.</p>
            <div className="dispositions">
              {dispositions.layouts.map((l) => (
                <button key={l.key} onClick={() => ajouterSection(l.key)}>
                  <span className="schema">
                    {l.colonnes.map((c, i) => (
                      <span key={i} style={{ flex: c }} />
                    ))}
                  </span>
                  <span>{l.titre}</span>
                </button>
              ))}
            </div>
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn" onClick={() => setNouvelleSection(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {ajout && (
        <div className="fenetre" role="dialog" aria-label="Ajouter un bloc">
          <div className="fenetre-corps large">
            <h2>Qu'ajoutez-vous dans cette colonne ?</h2>
            {catalogue.families.map((f) => (
              <div key={f.famille} className="famille">
                <h3>{f.famille}</h3>
                <div className="grille">
                  {f.items.map((it) => (
                    <button className="tuile" key={it.type} onClick={() => {
                      setBlocEdite({
                        bloc: { id: `${it.type}_${Date.now()}`, type: it.type, on: true,
                                ...(DEFAUTS[it.type] || {}) },
                        sectionId: ajout.sectionId, colonne: ajout.colonne,
                      })
                      setAjout(null)
                    }}>
                      <strong>{it.titre}</strong>
                      <span>{it.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn" onClick={() => setAjout(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {blocEdite && (
        <BlocForm bloc={blocEdite.bloc}
                  onEnregistrer={enregistrerBloc}
                  onSupprimer={dansLaPage(sections, blocEdite.bloc.id) ? supprimerBloc : null}
                  onAnnuler={() => setBlocEdite(null)} />
      )}
    </>
  )
}

function Couleurs({ site, verdict, sauver }) {
  return (
    <div className="carte">
      <h2>Couleur</h2>
      <div className="couleurs">
        {COULEURS.map((c) => (
          <button key={c} style={{ background: c }} aria-label={`Couleur ${c}`}
                  className={site.theme.primary === c ? 'on' : ''}
                  onClick={() => sauver({ theme: { ...site.theme, primary: c } })} />
        ))}
        <label className="perso">
          <input type="color" value={site.theme.primary || '#1B3B8F'}
                 onChange={(e) => sauver({ theme: { ...site.theme, primary: e.target.value } })} />
          <span aria-hidden="true">+</span>
          <span className="sr">Couleur personnalisée</span>
        </label>
      </div>
      <p className={`contraste ${verdict.corrige ? 'attention' : 'bon'}`} role="status">
        {verdict.corrige
          ? <>La teinte choisie n'était pas assez contrastée sur fond blanc ({verdict.origine} pour 1).
              Le portail utilise <strong>{verdict.couleur}</strong>, qui atteint {verdict.ratio} pour 1
              et respecte le référentiel d'accessibilité.</>
          : <>Contraste de {verdict.ratio} pour 1 sur fond blanc, au-dessus du minimum légal de 4,5.</>}
      </p>
    </div>
  )
}

function Identite({ site, sauver }) {
  const [envoi, setEnvoi] = useState(false)
  const majTheme = (champs) => sauver({ theme: { ...site.theme, ...champs } })

  return (
    <div className="carte">
      <h2>Identité</h2>
      {site.theme.logo_url && (
        <figure className="apercu-image">
          <img src={site.theme.logo_url} alt="Logo actuel" style={{ maxHeight: 90, objectFit: 'contain' }} />
        </figure>
      )}
      <div className="champ">
        <label htmlFor="logo">Logo ou blason de la commune</label>
        <input id="logo" type="file" accept="image/*" onChange={async (e) => {
          const f = e.target.files[0]
          if (!f) return
          setEnvoi(true)
          try { majTheme({ logo_url: (await upload(f)).url }) } finally { setEnvoi(false) }
        }} />
        <p className="aide">Un PNG ou un SVG à fond transparent rend mieux. {envoi && 'Envoi…'}</p>
        {site.theme.logo_url && (
          <button className="btn sm" onClick={() => majTheme({ logo_url: '' })}>Retirer le logo</button>
        )}
      </div>
      <div className="ligne-champs">
        <div className="champ">
          <label htmlFor="init">Initiales affichées si pas de logo</label>
          <input id="init" maxLength={3} defaultValue={site.theme.logo_text || ''}
                 onBlur={(e) => majTheme({ logo_text: e.target.value })} />
        </div>
        <div className="champ">
          <label htmlFor="ban">Bandeau d'accueil</label>
          <select id="ban" value={site.theme.banner || 'aplat'}
                  onChange={(e) => majTheme({ banner: e.target.value })}>
            <option value="aplat">Aplat de couleur</option>
            <option value="degrade">Dégradé</option>
            <option value="sobre">Fond clair</option>
          </select>
        </div>
      </div>
    </div>
  )
}

const libelle = (cat, type) =>
  cat.families.flatMap((f) => f.items).find((i) => i.type === type)?.titre
  || (type === 'hero' ? "Bandeau d'accueil" : type)

const trouverHero = (sections) =>
  sections.flatMap((s) => (s.colonnes || []).flat()).find((b) => b?.type === 'hero')

const dansLaPage = (sections, id) =>
  sections.some((s) => (s.colonnes || []).some((col) => col.some((b) => b.id === id)))

const DEFAUTS = {
  texte: { title: 'Le mot du maire', text: 'Écrivez ici ce que vous voulez dire aux habitants.' },
  image: { url: '', alt: '', caption: '', width: 'large' },
  logo: { width: 'moyenne', align: 'centre' },
  liens: { title: 'Liens utiles', items: [{ label: 'Le site de la commune', url: 'https://', hint: '' }] },
  chiffres: { title: '', items: [{ value: '1 240', label: 'habitants' }] },
  news: { title: 'Actualités', items: [{ t: '', d: '' }] },
  alerte: { text: '' },
  hero: { title: 'Vos démarches en ligne', lede: 'Déposez et suivez vos demandes sans vous déplacer.' },
  popular: { title: 'Les démarches les plus demandées', n: 6 },
  categories: { title: 'Toutes les démarches' },
}
